import API_BASE_URL from "./config";
import { getToken } from "../auth";

const SCRIPTURE_BASE = `${API_BASE_URL}/api/scriptures`;
const ASK_SHIA_BASE = `${API_BASE_URL}/api/ask-shia`;
const ADMIN_BASE = `${API_BASE_URL}/api/admin`;
const USER_HISTORY_BASE = `${API_BASE_URL}/api/user-history`;

// Helper: Get stored user info
export const getStoredUser = () => {
  try {
    const stored = localStorage.getItem("user");
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const getStoredUserId = () => {
  const user = getStoredUser();
  return user?.id || user?.userId || (user?.user && user.user.id) || null;
};

export const getStoredToken = () => {
  const user = getStoredUser();
  return getToken() || user?.token || user?.accessToken || null;
};

export const getAuthHeader = () => {
  const token = getStoredToken();
  return token
    ? {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    : {};
};

// SCRIPTURE API
export const scriptureAPI = {
  // ✅ now automatically includes logged-in userId if caller doesn't pass one
  askQuestion: async (question, userId = null) => {
    try {
      const effectiveUserId = userId || getStoredUserId();
      const headers = {
        "Content-Type": "application/json",
        ...getAuthHeader(),
      };

      const payload = {
        question,
        userId: effectiveUserId || null, // backend will ignore null (anonymous)
      };

      const response = await fetch(`${SCRIPTURE_BASE}/ask`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        console.error("Scripture ask error:", response.status, data);
        return { answer: "Failed to process question", error: true };
      }
      return data;
    } catch (err) {
      console.error("Scripture ask network error:", err);
      return { answer: "Service unavailable", error: true };
    }
  },

  // ✅ call correct backend endpoint: /api/user-history/{userId}/recent
  getRecentQuestions: async (userId) => {
    const effectiveUserId = userId || getStoredUserId();
    const headers = getAuthHeader();
    if (!effectiveUserId || !headers.Authorization) return { data: [] };

    try {
      const response = await fetch(`${USER_HISTORY_BASE}/${effectiveUserId}/recent`, {
        headers,
      });
      if (!response.ok) {
        console.error("Failed to load recent questions:", response.status);
        return { data: [] };
      }
      const json = await response.json();
      return { data: json };
    } catch (err) {
      console.error("Recent questions network error:", err);
      return { data: [] };
    }
  },

  checkHealth: async () => {
    try {
      const res = await fetch(`${SCRIPTURE_BASE}/health`);
      return { data: await res.text() };
    } catch {
      return { data: "Service unavailable" };
    }
  },
};

// ASK SHIA API (unchanged logic, just re-pasted for completeness)
export const askShiaAPI = {
  submitQuestion: async (userId, title, body, category, priority = "NORMAL") => {
    try {
      const headers = getAuthHeader();
      if (!headers.Authorization) {
        throw new Error("Please sign in to submit a question.");
      }

      const payload = { userId, title, body, category, priority };

      const response = await fetch(`${ASK_SHIA_BASE}/submit`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Failed to submit question");
      }

      const json = await response.json();
      return { data: json };
    } catch (err) {
      console.error("Ask Shia submit error:", err);
      throw err;
    }
  },

  getUserQuestions: async (userId) => {
    const headers = getAuthHeader();
    if (!userId || !headers.Authorization) return { data: [] };
    try {
      const response = await fetch(`${ASK_SHIA_BASE}/user/${userId}`, {
        headers,
      });
      if (!response.ok) {
        console.error("Failed to load user questions:", response.status);
        return { data: [] };
      }
      const json = await response.json();
      return { data: json };
    } catch (err) {
      console.error("User questions network error:", err);
      return { data: [] };
    }
  },

  getAllQuestions: async () => {
    try {
      const res = await fetch(`${ASK_SHIA_BASE}/admin/all`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) return { data: [] };
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: [] };
    }
  },

  getPendingQuestions: async () => {
    try {
      const res = await fetch(`${ASK_SHIA_BASE}/admin/pending`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) return { data: [] };
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: [] };
    }
  },

  postAnswer: async (questionId, answerData) => {
    try {
      const response = await fetch(
        `${ASK_SHIA_BASE}/admin/answer/${questionId}`,
        {
          method: "POST",
          headers: getAuthHeader(),
          body: JSON.stringify(answerData),
        }
      );

      if (!response.ok) throw new Error("Failed to submit answer");
      const json = await response.json();
      return { data: json };
    } catch (err) {
      console.error("Answer posting error:", err);
      throw err;
    }
  },

  checkHealth: async () => {
    try {
      const res = await fetch(`${ASK_SHIA_BASE}/health`);
      return { data: await res.text() };
    } catch {
      return { data: "Service unavailable" };
    }
  },
};

// ADMIN API (unchanged)
export const adminApi = {
  getDashboardStats: async () => {
    try {
      const res = await fetch(`${ADMIN_BASE}/dashboard/stats`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) return { data: {} };
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: {} };
    }
  },

  getRecentQuestions: async () => {
    try {
      const res = await fetch(`${ADMIN_BASE}/questions?size=10`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) return { data: [] };
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: [] };
    }
  },

  getRecentUsers: async () => {
    try {
      const res = await fetch(`${ADMIN_BASE}/users/recent`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) return { data: [] };
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: [] };
    }
  },

  getUsers: async () => {
    try {
      const res = await fetch(`${ADMIN_BASE}/users`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) return { data: [] };
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: [] };
    }
  },

  updateUserStatus: async (userId, isActive) => {
    try {
      const res = await fetch(`${ADMIN_BASE}/users/${userId}/status`, {
        method: "PUT",
        headers: getAuthHeader(),
        body: JSON.stringify({ isActive }),
      });
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: {} };
    }
  },

  updateUserRole: async (userId, role) => {
    try {
      const res = await fetch(`${ADMIN_BASE}/users/${userId}/role`, {
        method: "PUT",
        headers: getAuthHeader(),
        body: JSON.stringify({ role }),
      });
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: {} };
    }
  },

  getQuestions: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const res = await fetch(`${ADMIN_BASE}/questions?${queryParams}`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) return { data: [] };
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: [] };
    }
  },

  answerQuestion: async (questionId, answerData) => {
    try {
      const res = await fetch(`${ADMIN_BASE}/questions/${questionId}/answer`, {
        method: "POST",
        headers: getAuthHeader(),
        body: JSON.stringify(answerData),
      });
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: {} };
    }
  },

  updateQuestionStatus: async (questionId, status) => {
    try {
      const res = await fetch(`${ADMIN_BASE}/questions/${questionId}/status`, {
        method: "PUT",
        headers: getAuthHeader(),
        body: JSON.stringify({ status }),
      });
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: {} };
    }
  },

  getSearchHistory: async () => {
    try {
      const res = await fetch(`${ADMIN_BASE}/search-history`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) return { data: [] };
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: [] };
    }
  },

  getActivityLogs: async () => {
    try {
      const res = await fetch(`${ADMIN_BASE}/activity-logs`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) return { data: [] };
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: [] };
    }
  },

  getFeedback: async () => {
    try {
      const res = await fetch(`${ADMIN_BASE}/feedback`, {
        headers: getAuthHeader(),
      });
      if (!res.ok) return { data: [] };
      const json = await res.json();
      return { data: json };
    } catch {
      return { data: [] };
    }
  },
};

export default {
  scriptureAPI,
  askShiaAPI,
  adminApi,
  getStoredUser,
  getStoredUserId,
  getStoredToken,
  getAuthHeader,
};
